import {
  GRID_GAP,
  MAX_ASPECT_RATIO,
  MIN_ASPECT_RATIO,
  MIN_TILE_WIDTH,
} from './constants';

export interface TileLayout {
  rows: number;
  columns: number;
  tileWidth: number;
  tileHeight: number;
  paddingVertical: number;
  allocatedWidth: number;
  allocatedHeight: number;
}

/**
 * Calculates the optimal tile layout for a container
 * @param containerWidth - Width of the container
 * @param containerHeight - Height of the container
 * @param numberOfTiles - Total number of tiles to arrange
 * @returns TileLayout object containing rows, columns, and tile dimensions
 * @throws Error if input parameters are invalid
 */
export function calculateTileLayout(
  containerWidth: number,
  containerHeight: number,
  numberOfTiles: number,
): TileLayout {
  // Input validation
  if (containerWidth <= 0 || containerHeight <= 0 || numberOfTiles <= 0) {
    throw new Error(
      'Container dimensions and number of tiles must be positive',
    );
  }

  const totalArea = containerWidth * containerHeight;
  const tileArea = totalArea / numberOfTiles;
  const minArea = MIN_TILE_WIDTH * MIN_TILE_WIDTH * MIN_ASPECT_RATIO;
  if (tileArea < minArea) {
    const columnCount = Math.floor(containerWidth / MIN_TILE_WIDTH);
    const rowCount = Math.ceil(numberOfTiles / columnCount);
    const totalHorizontalGap = columnCount * GRID_GAP;
    const tileWidth = (containerWidth - totalHorizontalGap) / columnCount;
    const tileHeight = tileWidth * MIN_ASPECT_RATIO;

    return {
      rows: rowCount,
      columns: columnCount,
      tileWidth,
      tileHeight,
      paddingVertical: 0,
      allocatedWidth: containerWidth,
      allocatedHeight: containerHeight,
    };
  }

  // Initial grid calculation
  const squareRoot = Math.sqrt(numberOfTiles);
  let rowCount = Math.floor(squareRoot);
  let columnCount = Math.ceil(numberOfTiles / rowCount);

  // Adjust grid based on aspect ratio constraints
  let currentAspectRatio = getCalculatedAspectRatio(
    containerWidth,
    containerHeight,
    columnCount,
    rowCount,
  );

  // Optimize for tall containers
  if (currentAspectRatio < MIN_ASPECT_RATIO) {
    while (currentAspectRatio < MIN_ASPECT_RATIO && columnCount > 1) {
      columnCount--;
      rowCount = Math.ceil(numberOfTiles / columnCount);
      currentAspectRatio = getCalculatedAspectRatio(
        containerWidth,
        containerHeight,
        columnCount,
        rowCount,
      );
    }
  }
  // Optimize for wide containers
  else {
    while (
      currentAspectRatio > MAX_ASPECT_RATIO &&
      columnCount < numberOfTiles
    ) {
      columnCount++;
      rowCount = Math.ceil(numberOfTiles / columnCount);
      currentAspectRatio = getCalculatedAspectRatio(
        containerWidth,
        containerHeight,
        columnCount,
        rowCount,
      );
    }
  }

  // Optimize column count if possible
  const potentialRowCount = Math.ceil(numberOfTiles / (columnCount - 1));
  if (potentialRowCount === rowCount) {
    columnCount--;
  }

  // Calculate tile dimensions
  const totalHorizontalGap = columnCount * GRID_GAP;
  const totalVerticalGap = rowCount * GRID_GAP;

  let tileWidth = (containerWidth - totalHorizontalGap) / columnCount;
  let tileHeight = (containerHeight - totalVerticalGap) / rowCount;
  let paddingVertical = 0;

  // Adjust tile dimensions to maintain aspect ratio
  const tileAspectRatio = tileWidth / tileHeight;
  if (tileAspectRatio < 9 / 16) {
    // For extremely tall tiles
    paddingVertical =
      ((tileHeight - tileWidth * MAX_ASPECT_RATIO) * rowCount) / 2;
    tileHeight = tileWidth * MAX_ASPECT_RATIO;
  } else if (tileAspectRatio > MAX_ASPECT_RATIO) {
    // For extremely wide tiles
    tileWidth = tileHeight * MAX_ASPECT_RATIO;
  }

  // Calculate effective dimensions including gaps
  const allocatedWidth = tileWidth * columnCount + totalHorizontalGap;
  const allocatedHeight = tileHeight * rowCount + totalVerticalGap;

  return {
    rows: rowCount,
    columns: columnCount,
    tileWidth,
    tileHeight,
    paddingVertical,
    allocatedWidth,
    allocatedHeight,
  };
}

function getCalculatedAspectRatio(
  containerWidth: number,
  containerHeight: number,
  columnCount: number,
  rowCount: number,
): number {
  const totalHorizontalGap = columnCount * GRID_GAP;
  const totalVerticalGap = rowCount * GRID_GAP;
  const calculatedTileWidth =
    (containerWidth - totalHorizontalGap) / columnCount;
  const calculatedTileHeight = (containerHeight - totalVerticalGap) / rowCount;
  return calculatedTileWidth / calculatedTileHeight;
}
